import {
  Bell,
  Boxes,
  CircleHelp,
  FileText,
  FolderKanban,
  Image as ImageIcon,
  LayoutDashboard,
  ListChecks,
  LogOut,
  Mail,
  Menu,
  Palette,
  Settings,
  UserPlus,
  Vote,
} from 'lucide-react';
import { useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '@/components/confirm-dialog';
import { ProfileDialog } from '@/components/profile-dialog';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from '@/components/ui/sheet';
import { useMe } from '@/lib/api-hooks';
import { useAuth } from '@/lib/auth';
import { cn } from '@/lib/utils';

const navigation = [
  {
    name: 'Dashboard',
    href: '/',
    icon: LayoutDashboard,
    description: 'Overview of projects, voting and audience',
  },
  {
    name: 'Notifications',
    href: '/notifications',
    icon: Bell,
    description: 'Recent activity and admin alerts',
  },
  {
    name: 'Projects',
    href: '/projects',
    icon: FolderKanban,
    description: 'Create and manage projects through each stage',
  },
  {
    name: 'Artists',
    href: '/artists',
    icon: Palette,
    description: 'The artists taking part in projects',
  },
  {
    name: 'Proposals',
    href: '/proposals',
    icon: FileText,
    description: 'Competing submissions and their status',
  },
  {
    name: 'Media',
    href: '/media',
    icon: ImageIcon,
    description: 'Upload and organise the media library',
  },
  {
    name: 'Voting',
    href: '/voting',
    icon: Vote,
    description: 'Open voting periods and review results',
  },
  {
    name: 'Votes',
    href: '/votes',
    icon: ListChecks,
    description: 'Every vote cast during voting periods',
  },
  {
    name: 'Subscribers',
    href: '/subscribers',
    icon: UserPlus,
    description: 'The jury and mailing list',
  },
  {
    name: 'Emails',
    href: '/emails',
    icon: Mail,
    description: 'Voter emails captured after voting',
  },
  {
    name: 'Settings',
    href: '/settings',
    icon: Settings,
    description: 'Platform, notification and team settings',
  },
  {
    name: 'Help',
    href: '/help',
    icon: CircleHelp,
    description: 'About NeedfulThings and how to get in touch',
  },
];

function SidebarNav({
  onNavigate,
  onOpenProfile,
  onSignOut,
}: {
  onNavigate?: () => void;
  onOpenProfile?: () => void;
  onSignOut?: () => void;
}) {
  const { data: me } = useMe();
  const label = me?.name ?? me?.email ?? 'Profile';

  return (
    <>
      <div className="flex h-16 shrink-0 items-center gap-2 px-6">
        <Boxes className="size-6" />
        <span className="font-semibold tracking-tight">NeedfulThings</span>
      </div>
      <Separator className="shrink-0" />
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto p-3">
        {navigation.map((item) => (
          <NavLink
            key={item.href}
            to={item.href}
            end={item.href === '/'}
            onClick={onNavigate}
            className={({ isActive }) =>
              cn(
                'flex items-center gap-3 rounded-md px-3 py-2 font-medium text-sm transition-colors',
                isActive
                  ? 'bg-sidebar-accent text-sidebar-accent-foreground'
                  : 'text-muted-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
              )
            }
          >
            <item.icon className="size-4" />
            {item.name}
          </NavLink>
        ))}
      </nav>

      <Separator className="shrink-0" />
      <div className="shrink-0 p-2">
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            onOpenProfile?.();
          }}
          className="flex w-full items-center gap-3 rounded-md px-2 py-2 text-left transition-colors hover:bg-sidebar-accent"
        >
          <span className="flex size-8 shrink-0 items-center justify-center overflow-hidden rounded-full bg-muted font-medium text-sm">
            {me?.picture ? (
              <img src={me.picture} alt="" className="h-full w-full object-cover" />
            ) : (
              label.slice(0, 1).toUpperCase()
            )}
          </span>
          <span className="min-w-0">
            <span className="block truncate font-medium text-sm">{label}</span>
            <span className="block truncate text-muted-foreground text-xs">
              {me?.email ?? 'View profile'}
            </span>
          </span>
        </button>
        <button
          type="button"
          onClick={() => {
            onNavigate?.();
            onSignOut?.();
          }}
          className="mt-0.5 flex w-full items-center gap-2 rounded-md px-2 py-2 text-left font-medium text-muted-foreground text-sm transition-colors hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
        >
          <LogOut className="size-4" />
          Sign out
        </button>
      </div>
    </>
  );
}

export function DashboardLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const { clearSession } = useAuth();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const [signOutOpen, setSignOutOpen] = useState(false);
  const current = navigation.find((item) =>
    item.href === '/' ? location.pathname === '/' : location.pathname.startsWith(item.href),
  );

  function handleSignOut() {
    // `clearSession` wipes the query stores and resource cache, so the next
    // sign-in starts with default pagination/filters.
    clearSession();
    navigate('/login', { replace: true });
  }

  return (
    <div className="flex h-svh overflow-hidden bg-muted/30 [contain:paint]">
      <aside className="hidden h-svh w-64 shrink-0 flex-col border-r bg-sidebar md:flex">
        <SidebarNav
          onOpenProfile={() => setProfileOpen(true)}
          onSignOut={() => setSignOutOpen(true)}
        />
      </aside>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-64 gap-0 bg-sidebar p-0">
          <SheetTitle className="sr-only">Navigation</SheetTitle>
          <SidebarNav
            onNavigate={() => setMobileOpen(false)}
            onOpenProfile={() => setProfileOpen(true)}
            onSignOut={() => setSignOutOpen(true)}
          />
        </SheetContent>

        <div className="flex flex-1 flex-col overflow-hidden">
          <header className="shrink-0 border-b bg-background">
            <div className="mx-auto flex h-16 w-full max-w-7xl items-center justify-between gap-4 px-4 sm:px-6">
              <div className="flex items-center gap-2">
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="md:hidden">
                    <Menu className="size-5" />
                    <span className="sr-only">Open navigation</span>
                  </Button>
                </SheetTrigger>
                <div>
                  <h1 className="font-semibold text-lg">{current?.name ?? 'Admin'}</h1>
                  <p className="hidden text-muted-foreground text-xs sm:block">
                    {current?.description ?? 'Manage projects, artists and voting'}
                  </p>
                </div>
              </div>
            </div>
          </header>
          <main className="mx-auto w-full max-w-7xl flex-1 min-h-0 overflow-y-auto p-6">
            <Outlet />
          </main>
        </div>
      </Sheet>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />

      <ConfirmDialog
        open={signOutOpen}
        onOpenChange={setSignOutOpen}
        title="Sign out?"
        description="You'll need to sign in again to manage the CMS."
        confirmLabel="Sign out"
        confirmVariant="default"
        onConfirm={handleSignOut}
      />
    </div>
  );
}
